import React, { createContext, useContext, useState, useEffect } from "react";
import { useMap } from "@vis.gl/react-google-maps";

// Create the context
const DataLayerContext = createContext<{
  surfaceData: google.maps.Data | null;
} | null>(null);

// Custom hook to use the context
export const useMapDataLayer = () => {
  const context = useContext(DataLayerContext);
  if (!context) {
    throw new Error("useMapDataLayer must be used within a DataLayerProvider");
  }
  return context;
};

// Provider component
export const DataLayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const map = useMap();

  // Google Maps Data layer for surface features - using state so components re-render when it changes
  const [surfaceData, setSurfaceData] = useState<google.maps.Data | null>(null);

  // Create and manage the surface data layer - only recreate when map changes
  useEffect(() => {
    if (!map) return;

    // Create a new Data layer specifically for surface features
    const newSurfaceData = new google.maps.Data({
      map: map,
    });

    setSurfaceData(newSurfaceData);

    // Cleanup when component unmounts or map changes
    return () => {
      // Remove the data layer from the map
      if (newSurfaceData) {
        newSurfaceData.setMap(null);
      }
    };
  }, [map]); // Only depend on map, not event handlers

  const value = {
    surfaceData,
  };

  return (
    <DataLayerContext.Provider value={value}>
      {children}
    </DataLayerContext.Provider>
  );
};

export default DataLayerProvider;
